<template>
  <component
    :is="tag"
    :class="[{'element-hidden': !visability}]"
    class="mb-0"
    @click="initer"
  >
    <div
      v-if="show"
      class="position-relative text-container"
      :data-sid="section.sid"
      :class="[`--${section.sid}-margin`]"
    >
      <div
        v-if="type !== 'rich-text'"
        :class="[type, customClass, `--${section.sid}-text`, {'text-center': iconBox}]"
        :style="{...style}"
        class="text-ghost editor__content m-0"
        style="overflow-wrap: anywhere;"
      >
        <div
          class="ProseMirror"
          v-html="liveHtml"
        />
      </div>
      <editor-content
        ref="content"
        :class="[type, customClass, {'text-gradient': backgroundGradient}, `--${section.sid}-text`, {'text-center': iconBox}]"
        :editor="editor"
        :style="{...style, textShadow:'none'}"
        class="editor__content m-0"
        style="overflow-wrap: anywhere;"
      />
    </div>
  </component>
</template>

<script>
import _ from 'lodash'
import { mapState } from 'vuex'

import { Editor } from '@tiptap/core'
import { EditorContent } from '@tiptap/vue-2'
import { Plugin, PluginKey } from '@tiptap/pm/state'

import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Text from '@tiptap/extension-text'
import Blockquote from '@tiptap/extension-blockquote'
import Bold from '@tiptap/extension-bold'
import Code from '@tiptap/extension-code'
import CodeBlock from '@tiptap/extension-code-block'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import History from '@tiptap/extension-history'
import Italic from '@tiptap/extension-italic'
import Strike from '@tiptap/extension-strike'
import Underline from '@tiptap/extension-underline'
import OrderedList from '@tiptap/extension-ordered-list'
import BulletList from '@tiptap/extension-bullet-list'
import ListItem from '@tiptap/extension-list-item'
import TaskItem from '@tiptap/extension-task-item'
import TaskList from '@tiptap/extension-task-list'
import { TableKit } from '@tiptap/extension-table'

import BackgroundMixin from '@/components/mixins/BackgroundMixin'
import BorderMixin from '@/components/mixins/BorderMixin'
import ComponentProvider from '@builder/components/mixins/ComponentProvider'
import ResolutionMixin from '../../mixins/ResolutionMixin'

import TextColor from '@builder/plugins/TiptapTextColor'
import TextColorFix from '@builder/plugins/TiptapTextColorFix'
import FontSize from '@builder/plugins/TiptapFontSize'
import BackgroundColor from '@builder/plugins/TiptapBackgroundColor'
import FontWeight from '@builder/plugins/TiptapFontWeight'
import FontWeight2 from '@builder/plugins/TiptapFontWeight2'
import FontWeight3 from '@builder/plugins/TiptapFontWeight3'
import CustomLink from '@builder/plugins/TiptapCustomLink'
import Typed from '@/plugins/TiptapTyped'
import VerticalAlign from '@builder/plugins/TiptapVerticalAlign'
import TiptapFontFamily from '@builder/plugins/TiptapFontFamily'
import Selection from '@builder/plugins/TiptapSelection'
import TextTransform from '@/plugins/TiptapTextTransform'
import TextGradientColor from '@/plugins/TiptapTextGradientColor'
import TiptapGlobalVariable from '@/plugins/TiptapGlobalVariable'
import TiptapGlobalVariableLink from '@/plugins/TiptapGlobalVariableLink'

export default {
  name: 'ContentEditableV2',

  components: {
    EditorContent
  },

  mixins: [
    ComponentProvider,
    ResolutionMixin,
    BorderMixin,
    BackgroundMixin
  ],

  props: {
    value: {
      required: true
    },
    customs: {},
    section: {},
    uid: String,
    type: {
      type: String,
      default: 'paragraph'
    },
    iconBox: {
      type: Boolean,
      default: false
    },
    customClass: {
      type: String,
      default: ''
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      show: true,
      keepInBounds: true,
      editor: null,
      editorToDestroy: null,
      liveHtml: this.value || ''
    }
  },

  computed: {
    tag () {
      if (!this.customs.tag) {
        return this.type === 'headline' ? 'h2' : 'div'
      }

      return this.customs.tag
    },
    textShadow () {
      return _.get(this.customs.resolutionStyle[this.resolutioner], 'textShadow', {
        enabled: false,
        x: 0,
        y: 0,
        blur: 0,
        color: '#dddddd'
      })
    },
    backgroundGradient () {
      const _backgroundGradient = _.get(this.customs.resolutionStyle, [this.resolutioner, 'backgroundGradient'], this.customs.resolutionStyle.lg.backgroundGradient)
      const _backgroundGradientDirection = _.get(this.customs.resolutionStyle, [this.resolutioner, 'backgroundGradientDirection'], this.customs.resolutionStyle.lg.backgroundGradientDirection)
      if (_backgroundGradient) {
        const backgroundColorSubject = (attrs) => {
          if (!attrs) return null

          const gradient = () => {
            const values = attrs.map(gradient => {
              return `${gradient.color} ${gradient.position}%`
            })
            return `linear-gradient(${_backgroundGradientDirection ?? 90}deg,${values})`
          }

          return gradient()
        }

        return {
          backgroundImage: backgroundColorSubject(_backgroundGradient)
        }
      }

      return null
    },
    style () {
      return {
        textJustify: 'inter-word',
        textShadow: parseInt(this.textShadow.blur) > 0 ? `${this.textShadow.x}px ${this.textShadow.y}px ${this.textShadow.blur}px ${this.textShadow.color}` : null,
        ...this.backgroundGradient
      }
    },
    ...mapState('historyChanges', {
      historyDisabled: state => state.disable
    })
  },

  methods: {
    initer () {
      this.$store.commit('editor/SET_TIPTAP', this.editor)
    },
    isLegacyRedColor (value) {
      if (!value) return false

      const normalized = String(value).replace(/\s+/g, '').toLowerCase()
      return [
        'red',
        '#f00',
        '#ff0000',
        'rgb(255,0,0)',
        'rgba(255,0,0,1)',
        'rgba(255,0,0,1.0)'
      ].includes(normalized)
    },
    clearLegacyRedColor (html) {
      const content = typeof html === 'string' ? html : ''
      if (!content) return ''

      const tmp = document.createElement('DIV')
      tmp.innerHTML = content
      let changed = false

      tmp.querySelectorAll('span, spancolo').forEach((el) => {
        const styleColor = el.style ? el.style.color : ''
        const dataColor = el.getAttribute('data-color')
        const colorAttr = el.getAttribute('color')
        const hasLegacyRedColor = this.isLegacyRedColor(styleColor) ||
          this.isLegacyRedColor(dataColor) ||
          this.isLegacyRedColor(colorAttr)

        if (!hasLegacyRedColor) return

        changed = true

        if (el.style) {
          el.style.removeProperty('color')
          if (!el.getAttribute('style') || !el.getAttribute('style').trim()) {
            el.removeAttribute('style')
          }
        }

        if (this.isLegacyRedColor(dataColor)) {
          el.removeAttribute('data-color')
        }

        if (this.isLegacyRedColor(colorAttr)) {
          el.removeAttribute('color')
        }
      })

      return changed ? tmp.innerHTML : content
    }
  },

  watch: {
    historyDisabled () {
      setTimeout(() => {
        this.editor.setContent(this.clearLegacyRedColor(this.value || ''))
      })
    },
    value (newVal) {
      const next = this.clearLegacyRedColor(newVal || '')
      this.liveHtml = next

      if (!this.editor) return

      const current = this.editor.getHTML()

      if (current !== next) {
        this.editor.commands.setContent(next, false)
      }
    }
  },

  mounted () {
    const rawContent = this.value || ''
    const initialContent = this.clearLegacyRedColor(rawContent)

    if (initialContent !== rawContent) {
      this.liveHtml = initialContent
      this.$emit('input', initialContent)
    }

    this.editor = new Editor({
      editable: !this.readonly,
      extensions: [
        Document,
        Paragraph,
        Text,
        Selection,
        Blockquote,
        CodeBlock,
        HardBreak,
        Heading.configure({
          levels: [1, 2, 3, 4, 5, 6]
        }),
        Bold,
        Code,
        Italic,
        Strike,
        TextColor,
        TextColorFix,
        TextGradientColor,
        Underline,
        History,
        FontSize,
        BackgroundColor,
        FontWeight,
        FontWeight2,
        FontWeight3,
        CustomLink,
        TextTransform,
        TiptapFontFamily,
        Typed,
        VerticalAlign,
        OrderedList,
        BulletList,
        ListItem,
        TaskItem,
        TaskList,
        TableKit.configure({
          resizable: true
        }),
        TiptapGlobalVariable,
        TiptapGlobalVariableLink
        // new TextBackground()
      ],
      onCreate: ({ editor }) => {
        this.liveHtml = editor.getHTML()
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML()
        const sanitized = this.clearLegacyRedColor(html)

        if (sanitized !== html) {
          editor.commands.setContent(sanitized, false)
          return
        }

        this.liveHtml = sanitized
        this.$emit('input', sanitized)
      },

      parseOptions: {
        preserveWhitespace: 'full'
      },
      content: initialContent
    })

    VEvent.listen(this.uid, () => {
      this.initer()
    })

    const handleDomEvents = (view) => {
      const colgroups = [...view.dom.querySelectorAll('colgroup')]

      if (colgroups.length) {
        let tmp = document.createElement('DIV')
        tmp.innerHTML = this.value

        const tables = [...tmp.querySelectorAll('table')]
        tables.forEach((table, index) => {
          const colgroup = table.querySelector('colgroup')
          if (colgroup) {
            colgroup.innerHTML = colgroups[index].innerHTML
          } else {
            table.appendChild(colgroups[index])
          }

          table.style.width = colgroups[index].closest('table').style.width
        })

        setTimeout(() => {
          this.$emit('input', tmp.innerHTML)
        })
      }
    }

    const vm = this
    if (!this.editor) {
      // Editor failed to initialize for some reason; avoid runtime error.
      return
    }
    this.editor.registerPlugin(new Plugin({
      key: new PluginKey('codeClearOnPaste'),
      props: {
        handleDOMEvents: {
          textInput: handleDomEvents,
          mouseup: handleDomEvents
        },
        transformPastedHTML: function (html) {
          // If this component is used as rich-text, keep the pasted HTML as-is
          if (vm && vm.type === 'rich-text') {
            return html
          }

          // Otherwise, strip HTML to plain text
          let tmp = document.createElement('DIV')
          tmp.innerHTML = html
          return tmp.textContent || tmp.innerText || ''
        }
      }
    }))
  },

  // beforeDestroy () {
  //   this.editor.destroy()
  // },

  beforeDestroy () {
    // Parent beforeDestroy runs before children; clear the prop so <EditorContent>
    // doesn't try to touch `editor.view.dom` during its own teardown.
    this.editorToDestroy = this.editor
    this.editor = null
    this.$store.commit('editor/SET_TIPTAP', null)
  },

  destroyed () {
    const editor = this.editorToDestroy
    this.editorToDestroy = null
    if (!editor) return

    try {
      editor.destroy()
    } catch (e) {
      // During very fast mount/unmount (e.g. duplicate/remove), tiptap may not have a mounted view.
      // Swallow only this known error; log anything else.
      const msg = e && typeof e.message === 'string' ? e.message : ''
      if (msg.includes('The editor view is not available')) return
      // eslint-disable-next-line no-console
      console.error(e)
    }
  }
}
</script>

<style>
.editor__content:not(.text-ghost) bg-marker {
  background: none !important;
}

.editor__content span-var {
  display: inline-block;
  padding: 2px 4px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: invert(0.15) grayscale(0.15);
  color: rgba(0, 0, 0, 0.75);
}

.editor__content gradient-span,
.editor__content spans {
  display: inline-block;
}
</style>
